<template>
  <!--
    Spec 6.7, screen 7, second face: what an approved device shows when a reset
    has been asked for — "at app launch rather than on entering Next Steps,
    because the member has to see it".

    MOUNTED BY App.vue, not by a Next Steps page, and that is the whole point.
    A reset deletes everything the member has written, on a seven-day clock, and
    the member it has to reach is exactly the one who was not going to open Next
    Steps this week. So this sits in the app shell, where every launch renders
    it.

    `PendingDeviceRequestHost.vue` was the same shape one requirement weaker —
    gated on the chooser and Hub routes, because a device waiting for approval
    is waiting on a member who is using the feature. #1691 took that gate off:
    the member is standing there holding the new phone, and this old one is the
    only thing that can answer, so it too is asked on any route now.

    WHICH MAKES THIS ONE FIRST. Both are app-shell modals over the same member,
    so a reset and a waiting device can now be due at the same launch. This
    holds the screen and publishes `courses-devices/resetNoticeOnScreen` while
    it does; the request waits for it. Destructive before merely useful — the
    sentence about a member's notes being deleted is not something to cover
    with a question about a phone.

    A MODAL, because `ResetPending.vue` is an `ion-page`: an ion-modal is the
    one container an ion-page belongs in, and this has to sit over whatever the
    member was doing rather than beneath it. Teleported to `ion-app` and
    presented programmatically, per docs/ION_MODAL_PATTERN.md — `:is-open`
    silently does nothing when it flips after mount, which is every case here.

    `defer` IS LOAD-BEARING, and this component is the reason that doc now says
    so. App.vue renders this as a child of its own `<ion-app>`, and Vue mounts
    an element's children BEFORE inserting the element — so an undeferred
    teleport resolves `document.querySelector('ion-app')` to null, warns
    "Invalid Teleport target on mount", never mounts the modal at all, and
    never re-resolves the target on any later patch. The notice then does not
    exist on any device, which is the whole of screen 7's second face. `defer`
    resolves the target in a post-render effect instead, by which time
    `ion-app` is in the document. Every other ion-app-child modal in the app
    carries it (TimeoutModal, ForceUpdateModal, Modal, CgHardBlockModal — all
    fixed the same way by dcb0be20a — and PendingDeviceRequestHost).

    The teleport is why the mixin AND the v-bind are both needed: content
    teleported out of the page root leaves the page's replay-privacy stamp
    behind, so this binds its own. The device NAME the member gave the phone the
    reset came from is the personal data in question.
  -->
  <Teleport defer to="ion-app">
    <ion-modal
      ref="ionModal"
      v-bind="privateRegionAttrs"
      :can-dismiss="true"
      @ionModalDidDismiss="onModalDismissed"
    >
      <ResetPending
        :pending-reset="pendingReset"
        :device-name="requestingDevice"
        :asked="false"
        :can-cancel="true"
        :busy="busy"
        :error="failure"
        @cancel="onCancel"
        @dismiss="onWasMe"
      />
    </ion-modal>
  </Teleport>
</template>

<script>
import { IonModal } from '@ionic/vue';
import { mapGetters } from 'vuex';
import privateRegion from '@/mixins/PrivateRegion';
import ResetPending from '@/pages/courses/encryption/ResetPending.vue';
import { requestingDeviceName } from '@/pages/courses/encryption/reset-copy';
import { RESET_OUTCOME, cancelReset } from '@/utils/courses-encryption/reset';
import {
  dismissResetNotice,
  isResetNoticeDismissed,
} from '@/utils/courses-encryption/reset-notice';

/** A failure this host made itself, with no API error to describe it. */
const LOCAL_FAILURE = Object.freeze({ code: 'unknown', message: null });

export default {
  name: 'ResetPendingNoticeHost',
  components: { IonModal, ResetPending },
  mixins: [privateRegion],
  data() {
    return {
      busy: false,
      failure: null,
      /**
       * The hold this device has been told about, as `requestedAt`. Kept in
       * data because the record lives in `localStorage`, which is not
       * reactive: a dismissal has to close the modal now, and the same value
       * read back on the next launch has to keep it shut.
       */
      acknowledged: null,
    };
  },
  computed: {
    ...mapGetters('courses-devices', ['pendingReset', 'devices']),
    ...mapGetters('courses-keys', ['hasMasterKey', 'memberId']),
    /** The name for the spec's sentence, or null to use its fallback phrase. */
    requestingDevice() {
      return requestingDeviceName(this.pendingReset, this.devices);
    },
    /**
     * Whether the hold was started from THIS install. It is not shown the
     * notice: it is the device that asked, so it already knows, and screen 7's
     * other face is what the Start over route gives it.
     */
    askedFromHere() {
      const asked = this.pendingReset?.requestedBy;
      const own = this.$store.state['courses-devices'].localDevice?.id;
      return !!asked && !!own && asked === own;
    },
    /**
     * `hasMasterKey` is what "approved device" means, and it is also what makes
     * Cancel possible at all — `DELETE /reset` answers 403 without a signature
     * the bundle key makes. A device with neither the key nor a reason to be
     * told is shown nothing.
     */
    visible() {
      if (!this.hasMasterKey || !this.pendingReset || this.askedFromHere) return false;
      if (this.acknowledged === this.pendingReset.requestedAt) return false;
      /*
       * READ STRAIGHT FROM THE RECORD, not from `acknowledged` alone.
       *
       * A NEW hold is a new notice, even on a device that dismissed the last
       * one: a reset cancelled and asked for again is a second reset, and the
       * member has to be told as though the first had never happened. So the
       * dismissal is compared against the live hold rather than trusted as a
       * flag — and it is compared HERE rather than in a watcher, because a
       * computed that waited on a watcher having run first was wrong in the one
       * order a real launch always takes: this mounts at boot with no status
       * snapshot, so the hold arrives later, and whichever watcher job Vue ran
       * first decided the answer. It presented the notice and then dismissed
       * it, so a member who had already answered "This was me" watched a
       * full-screen modal about their notes being deleted animate in and out on
       * every single launch.
       *
       * `acknowledged` stays as the reactive nudge a tap needs: `localStorage`
       * is not reactive, so nothing else would re-evaluate this.
       */
      return !isResetNoticeDismissed(this.memberId, this.pendingReset);
    },
  },
  watch: {
    /**
     * DECLARED BEFORE `visible`, so its job runs first — watcher jobs run in
     * declaration order, and `visible` must never be evaluated against the
     * previous hold's `acknowledged`. `visible` no longer depends on this
     * having run (see above); the order is belt as well as braces.
     */
    pendingReset: {
      immediate: true,
      handler() {
        this.failure = null;
        this.acknowledged = null;
      },
    },
    visible(open) {
      this.toggle(open);
    },
  },
  mounted() {
    // The app shell mounts this once, at boot, so `mounted` runs before any
    // status read has landed — but a read that landed first (a resumed session,
    // a Next Steps screen that got there before the watcher) would otherwise
    // leave the modal shut over a real hold.
    //
    // On `$nextTick`, because the teleport is `defer`red: the modal is mounted
    // into `ion-app` in a post-render effect, so at `mounted()` there may be no
    // element to present yet.
    this.$nextTick(() => {
      if (this.visible) this.toggle(true);
    });
  },
  methods: {
    /**
     * Presents or dismisses, and says so: `PendingDeviceRequestHost` is an
     * app-shell modal on every route too since #1691, and it waits on this.
     *
     * The flag follows what is ACTUALLY on screen, never what ought to be — an
     * `open` with no element to present is still nothing on screen, and
     * publishing it as true would leave the device request suppressed for the
     * rest of the session over a notice the member never saw.
     */
    toggle(open) {
      const modal = this.$refs.ionModal?.$el;
      if (!modal) {
        // Nothing on screen, and nothing that will ever fire
        // `ionModalDidDismiss` to say so. Release the request here or it waits
        // for the rest of the session on a notice the member never saw.
        this.$store.commit('courses-devices/setResetNoticeOnScreen', false);
        return;
      }
      if (open) {
        modal.present();
        this.$store.commit('courses-devices/setResetNoticeOnScreen', true);
        return;
      }
      // The `false` is NOT committed here. `dismiss()` starts an animation of a
      // few hundred milliseconds, and the notice is still covering the screen
      // for all of it; releasing the request now would present it over a notice
      // on its way out. `onModalDismissed` commits the false when the modal has
      // actually gone, and Ionic fires it for a programmatic dismiss as well as
      // a swipe.
      modal.dismiss();
    },
    /**
     * A member who swipes the modal away has answered NEITHER way: the hold
     * goes on and the notice returns on the next launch. Nothing is recorded —
     * a swipe is not "this was me", and a deletion is the wrong thing to stop
     * asking about on a gesture.
     */
    onModalDismissed() {
      this.busy = false;
      // Off the screen by any route — Cancel, "This was me", or the swipe this
      // comment is about — so the device request behind it may be asked now
      // (#1691). A swipe still records nothing: the hold goes on and the notice
      // returns on the next launch, but it is not holding the screen meanwhile.
      this.$store.commit('courses-devices/setResetNoticeOnScreen', false);
    },
    async onCancel() {
      if (this.busy) return;
      this.busy = true;
      this.failure = null;
      try {
        const result = await cancelReset({ store: this.$store });
        // A successful cancel refreshes the status itself, so `pendingReset`
        // goes null and `visible` closes the modal with nothing to do here.
        if (result.outcome !== RESET_OUTCOME.CANCELLED) {
          this.failure = result.error ?? LOCAL_FAILURE;
        }
      } finally {
        this.busy = false;
      }
    },
    /**
     * "This was me" — THIS DEVICE ONLY (spec 6.7, screen 7). The reset goes on;
     * what changes is that this phone stops asking, across relaunches, for this
     * hold and this member.
     */
    onWasMe() {
      dismissResetNotice(this.memberId, this.pendingReset);
      this.acknowledged = this.pendingReset?.requestedAt ?? null;
    },
  },
};
</script>
